import * as React from 'react';

import { cn } from './cn';

import { Icon } from './Icon';
import { Text } from './Typography';

const isExternalHref = href => /^https?:\/\//u.test(href ?? '');

// Appearance × state styling. `default` is the canonical link affordance
// (primary colour, underline on hover/focus). `bare` is the chromeless
// variant — inherits the surrounding text colour, no underline, swap
// the focus indicator for a ring. Use bare when the surrounding context
// already signals interactivity (sortable table header, an in-cell
// click target) and a primary-coloured / underlined link would compete
// visually.
const APPEARANCE_CLASSES = {
    default: {
        color: 'tw-text-primary',
        interaction: 'tw-underline-offset-4 hover:tw-underline focus-visible:tw-underline'
    },
    bare: {
        color: 'tw-text-inherit',
        interaction: 'tw-rounded-sm hover:tw-text-foreground focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 tw-ring-offset-background'
    }
};

const Link = React.forwardRef(({
    appearance = 'default',
    className: _className,
    style: _style,
    href,
    icon,
    loading = false,
    onClick,
    rel,
    target,
    text,
    variant = 'control',
    ...props
}, ref) => {
    const disabled = loading || props['aria-disabled'] === true;
    const appearanceClasses = APPEARANCE_CLASSES[appearance] ?? APPEARANCE_CLASSES.default;
    const sharedClassName = cn(
        'tw-inline-flex tw-items-center tw-gap-1 tw-transition-colors focus-visible:tw-outline-none',
        disabled
            ? 'tw-cursor-not-allowed tw-text-muted-foreground'
            : `tw-cursor-pointer ${appearanceClasses.color} ${appearanceClasses.interaction}`
    );

    if (href && !onClick) {
        return (
            <a
                {...props}
                ref={ref}
                aria-disabled={disabled}
                className={sharedClassName}
                href={href}
                rel={rel ?? (isExternalHref(href) ? 'noreferrer noopener' : undefined)}
                target={target ?? (isExternalHref(href) ? '_blank' : undefined)}
            >
                {icon ? <Icon icon={icon} size="small" tone="inherit" /> : null}
                <Text tone="inherit" variant={variant}>{text}</Text>
            </a>
        );
    }

    return (
        <button
            {...props}
            ref={ref}
            aria-disabled={disabled}
            className={sharedClassName}
            disabled={disabled}
            onClick={event => {
                if (!disabled) {
                    onClick?.(event);
                }
            }}
            type="button"
        >
            {icon ? <Icon icon={icon} size="small" tone="inherit" /> : null}
            <Text tone="inherit" variant={variant}>{text}</Text>
        </button>
    );
});

Link.displayName = 'Link';

export { Link };
